import { Box, Button, TextField } from "@mui/material";
import type { KeyboardEvent } from "react";
import { useThemeContext } from "../../../globalContext/global-context";
import { OverlaySlot } from "../../Wrappers/OverlaySlot";
import Hints from "../Hints";
import { ModalButton, ModalText, TextInput } from "./style";

type CustomAnswerModalProps = {
  open: boolean;
  prompt: string;
  value: string;
  error?: string;
  submitDisabled?: boolean;
  onChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  onRemove?: () => void;
};

const confirmOnEnter = (
  event: KeyboardEvent,
  value: string,
  disabled: boolean,
  submit: () => void,
) => {
  if (
    event.key === "Enter" &&
    !event.nativeEvent.isComposing &&
    value.trim() &&
    !disabled
  ) {
    event.preventDefault();
    submit();
  }
};

const CustomAnswerContent = ({
  prompt,
  value,
  error,
  submitDisabled = false,
  onChange,
  onSubmit,
  onRemove,
}: Omit<CustomAnswerModalProps, "open" | "onClose">) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const primaryColor = theme.tokens.color.action.primary;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: `${theme.tokens.spacing["4"]}px`,
        padding: `${theme.tokens.spacing["4"]}px`,
      }}
    >
      <Hints text={prompt} sx={ModalText(theme)} variant="label" />
      <TextField
        autoFocus
        data-testid="other-input-box"
        value={value}
        disabled={submitDisabled}
        error={Boolean(error)}
        helperText={error}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) =>
          confirmOnEnter(event, value, submitDisabled, onSubmit)
        }
        placeholder={theme.copy.hints.customAnswer}
        slotProps={{ htmlInput: { "aria-label": prompt } }}
        variant="outlined"
        sx={{
          ...TextInput(theme, primaryColor),
          width: "90%",
          textAlign: "center",
        }}
      />
      <Button
        data-testid="other-submit-btn"
        sx={{
          ...ModalButton(theme),
          backgroundColor: value.trim()
            ? primaryColor
            : theme.tokens.color.border.strong,
        }}
        disabled={!value.trim() || submitDisabled}
        onClick={onSubmit}
      >
        {theme.copy.actions.confirm}
      </Button>
      {onRemove && (
        <Button
          data-testid="other-remove-btn"
          disabled={submitDisabled}
          onClick={onRemove}
        >
          {theme.copy.actions.remove}
        </Button>
      )}
    </Box>
  );
};

const CustomAnswerModal = ({
  open,
  onClose,
  ...content
}: CustomAnswerModalProps) => {
  const { globalObject } = useThemeContext();
  return (
    <OverlaySlot
      open={open}
      onClose={onClose}
      closeLabel={globalObject.themeData.normalizedTheme.copy.actions.close}
      closeTestId="other-close-btn"
      testId="other-modal-box"
      ariaLabel={content.prompt}
    >
      <CustomAnswerContent {...content} />
    </OverlaySlot>
  );
};

export default CustomAnswerModal;
